import React, { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';
import { useStore } from '../contexts/StoreContext';

interface HeaderProps {
  onToggleSidebar: () => void;
  onToggleCart: () => void;
  children?: ReactNode;
}

const Header: React.FC<HeaderProps> = ({ children, onToggleSidebar, onToggleCart }) => {
  const location = useLocation();
  const { t } = useLanguage();
  const { getTotalItems } = useStore();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-40">
      {/* Top line */}
      <div className="bg-white text-black py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {/* Phone number */}
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
              </svg>
              <div>
                <div className="font-bold">
                  <a href="tel:+351289314500" className="text-black no-underline opacity-90 hover:opacity-100 hover:text-secondary transition-all duration-300 text-3sm">+351 289 314 500</a>
                </div>
                <div className="text-sm opacity-75">{t.header.phoneLabel}</div>
              </div>
            </div>
          </div>
          {/* Language selector */}
          <div className="absolute top-0 right-0 z-[70] hidden md:block lg:block">
            <LanguageSwitcher variant="header" />
            <div className="text-secondary font-bold text-xl py-2 text-right pr-5" style={{ fontFamily: "'Tropika Script', cursive" }}>
              We ❤️ Vilamoura ...
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white relative z-[60]">
        <div 
          className="relative"
          style={{
            background: 'white url(https://www.cleverdetails.com/images/tiltedwall.png) bottom left no-repeat',
            backgroundSize: 'contain',
          }}
        >
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={onToggleSidebar}
                  className="text-primary hover:text-secondary transition-colors p-2"
                  aria-label="Toggle Menu"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                </button>
                <Link to="/" className="block">
                  <img 
                    src="https://www.cleverdetails.com/images/logo.png" 
                    alt="Clever Details" 
                    className="h-20 md:h-24 w-auto pb-4"
                    loading="eager"
                    decoding="async"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "/images/logo.png";
                    }}
                  />
                </Link>
              </div>
              <img 
                src="https://www.cleverdetails.com/images/header-right.png" 
                alt="Clever Details" 
                className="hidden md:block h-16 md:h-20 w-auto"
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "/images/header-right.png";
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Bar - Positioned to hover over the header */}
      <nav className="absolute bottom-0 left-0 right-0 transform translate-y-1/2 z-[50] pt-12 pb-2 shadow-lg bg-primary">
        {/* Desktop Navigation */}
        <div className="hidden lg:block mx-auto">
          <div className="flex justify-center items-center gap-4 md:gap-8 px-4 md:px-8 py-3 overflow-x-auto whitespace-nowrap">
            <Link 
              to="/all" 
              className={`${isActive('/all') ? 'text-secondary' : 'text-white'} hover:text-secondary transition-colors font-medium text-xl md:text-3xl`}
            >
              {t.header.menu.advantages}
            </Link>
            <Link 
              to="/shopping" 
              className={`${isActive('/shopping') ? 'text-secondary' : 'text-white'} hover:text-secondary transition-colors font-medium text-xl md:text-3xl`}
            >
              {t.shopping.title}
            </Link>
            <Link 
              to="/access" 
              className={`${isActive('/access') ? 'text-secondary' : 'text-white'} hover:text-secondary transition-colors font-medium text-xl md:text-3xl`}
            >
              {t.header.menu.access}
            </Link>
            <Link 
              to="/partnership" 
              className={`${isActive('/partnership') ? 'text-secondary' : 'text-white'} hover:text-secondary transition-colors font-medium text-xl md:text-3xl`}
            >
              {t.header.menu.partnership}
            </Link>
            <Link 
              to="/about" 
              className={`${isActive('/about') ? 'text-secondary' : 'text-white'} hover:text-secondary transition-colors font-medium text-xl md:text-3xl`}
            >
              {t.header.menu.about}
            </Link>
            <Link 
              to="/contact" 
              className={`${isActive('/contact') ? 'text-secondary' : 'text-white'} hover:text-secondary transition-colors font-medium text-xl md:text-3xl`}
            >
              {t.header.menu.contact}
            </Link>
            
            {/* Cart Button */}
            <button
              onClick={onToggleCart}
              className="text-white hover:text-secondary transition-colors p-2 relative ml-4"
              aria-label="Toggle Cart"
            >
              <svg 
                className="w-6 h-6" 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path fillRule="evenodd" clipRule="evenodd" d="M3.25 4C3.25 3.58579 3.58579 3.25 4 3.25H5.14208C6.00583 3.25 6.74039 3.88019 6.87173 4.7339L7.11776 6.33306C7.28591 6.2792 7.46564 6.25 7.65301 6.25H17.347C18.412 6.25 19.23 7.19323 19.0794 8.24749L18.6104 11.5303C18.5185 12.1738 18.2666 12.7597 17.8985 13.25H18C19.2426 13.25 20.25 14.2574 20.25 15.5C20.25 16.5376 19.5476 17.4112 18.5924 17.6712C18.6941 17.9277 18.75 18.2073 18.75 18.5C18.75 19.7426 17.7426 20.75 16.5 20.75C15.2574 20.75 14.25 19.7426 14.25 18.5C14.25 18.237 14.2951 17.9846 14.378 17.75H10.622C10.7049 17.9846 10.75 18.237 10.75 18.5C10.75 19.7426 9.74264 20.75 8.5 20.75C7.25736 20.75 6.25 19.7426 6.25 18.5C6.25 17.2574 7.25736 16.25 8.5 16.25H18C18.4142 16.25 18.75 15.9142 18.75 15.5C18.75 15.0858 18.4142 14.75 18 14.75H8.3673C7.49641 14.75 6.75805 14.1096 6.63488 13.2475L6.25811 10.6101L5.38917 4.96199C5.37041 4.84003 5.26547 4.75 5.14208 4.75H4C3.58579 4.75 3.25 4.41421 3.25 4ZM7.74266 10.3953L8.11981 13.0354C8.1374 13.1585 8.24288 13.25 8.3673 13.25H14.8981C16.0178 13.25 16.9671 12.4267 17.1255 11.3182L17.5945 8.03535C17.616 7.88475 17.4991 7.75 17.347 7.75H7.65301C7.50087 7.75 7.38401 7.88475 7.40552 8.03536L7.74231 10.3929C7.74243 10.3937 7.74254 10.3945 7.74266 10.3953ZM16.5 17.75C16.0858 17.75 15.75 18.0858 15.75 18.5C15.75 18.9142 16.0858 19.25 16.5 19.25C16.9142 19.25 17.25 18.9142 17.25 18.5C17.25 18.0858 16.9142 17.75 16.5 17.75ZM8.5 17.75C8.08579 17.75 7.75 18.0858 7.75 18.5C7.75 18.9142 8.08579 19.25 8.5 19.25C8.91421 19.25 9.25 18.9142 9.25 18.5C9.25 18.0858 8.91421 17.75 8.5 17.75Z"/>
              </svg>
              {getTotalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-secondary text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold z-[9999] hover:scale-110 transition-all duration-200" style={{ right: '-8px', top: '2px' }}>
                  {getTotalItems}
                </span>
              )}
            </button>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header; 